// Thread API module: typed access to the /sessions endpoints.
// Takes an injected ApiClient — see entities/_api-injection.
// See react-rewrite-plan.md §11.3 (api-entities-threads-chat).

import type { ApiClient } from '@/shared/api';
import {
  toThreadDetailView,
  toThreadSummaryList,
} from '../model/toThreadView';
import type { ThreadDetailView, ThreadSummaryView } from '../model/types';

export interface ListThreadsOptions {
  limit?: number;
  signal?: AbortSignal;
}

// Phase B reconciliation: the wt-3b `entity-thread-queries` merge replaced
// the wt-3a `ThreadSummary`/`ThreadDetail` shapes with `ThreadSummaryView`/
// `ThreadDetailView` (ReadonlyArray-typed). This factory now produces the
// new shapes so the wt-3a entity-API scaffolding stays compilable.
export interface ThreadApi {
  createThread(agentId: string, signal?: AbortSignal): Promise<string>;
  listThreads(options?: ListThreadsOptions): Promise<ReadonlyArray<ThreadSummaryView>>;
  getThread(threadId: string, signal?: AbortSignal): Promise<ThreadDetailView>;
  deleteThread(threadId: string, signal?: AbortSignal): Promise<void>;
  generateTitle(threadId: string, signal?: AbortSignal): Promise<string | null>;
  updateTitle(threadId: string, title: string, signal?: AbortSignal): Promise<void>;
  updatePin(threadId: string, pinned: boolean, signal?: AbortSignal): Promise<void>;
}

export class ThreadNotFoundError extends Error {
  constructor(public readonly threadId: string) {
    super(`Thread ${threadId} not found`);
    this.name = 'ThreadNotFoundError';
  }
}

function threadPath(threadId: string, suffix = ''): string {
  return `/api/v1/sessions/${encodeURIComponent(threadId)}${suffix}`;
}

export function createThreadApi(client: ApiClient): ThreadApi {
  return {
    async createThread(agentId, signal) {
      const raw = await client.request<{ session_id?: unknown }>({
        method: 'POST',
        path: '/api/v1/sessions',
        body: { agent_id: agentId },
        signal,
      });
      if (typeof raw?.session_id !== 'string' || raw.session_id.length === 0) {
        throw new Error('Thread create response did not include session_id');
      }
      return raw.session_id;
    },

    async listThreads(options = {}) {
      const raw = await client.request({
        path: '/api/v1/sessions',
        query: options.limit !== undefined ? { limit: options.limit } : undefined,
        signal: options.signal,
      });
      return toThreadSummaryList(raw);
    },

    async getThread(threadId, signal) {
      const raw = await client.request({
        path: threadPath(threadId),
        signal,
      });
      const detail = toThreadDetailView(raw);
      if (detail === null) {
        throw new ThreadNotFoundError(threadId);
      }
      return detail;
    },

    async deleteThread(threadId, signal) {
      await client.request({
        method: 'DELETE',
        path: threadPath(threadId),
        signal,
      });
    },

    async generateTitle(threadId, signal) {
      const raw = await client.request<{ title?: unknown }>({
        method: 'POST',
        path: threadPath(threadId, '/generate-title'),
        signal,
      });
      return typeof raw?.title === 'string' ? raw.title : null;
    },

    async updateTitle(threadId, title, signal) {
      await client.request({
        method: 'PATCH',
        path: threadPath(threadId),
        body: { title },
        signal,
      });
    },

    async updatePin(threadId, pinned, signal) {
      await client.request({
        method: 'PATCH',
        path: threadPath(threadId),
        body: { pinned },
        signal,
      });
    },
  };
}
